<template>
  <validation-observer ref="form">
    <b-form @submit.prevent="handleAddMember">
      <b-overlay
        :show="loadingUsers || isProcessing"
        rounded="sm"
      >
        <b-row>
          <b-col md="10">
            <validation-provider
              v-slot="{ errors }"
              name="Members"
              rules="required"
            >
              <b-form-group
                :label="$t('Add members')"
                label-for="select-members"
              >
                <v-select
                  id="select-members"
                  v-model="selectedMembers"
                  :options="availableMembers"
                  label="name"
                  :placeholder="$t('Search and select members')"
                  multiple
                  :reduce="option => option"
                  :filterable="true"
                  :clearable="true"
                >
                  <template #option="{ name, email, avatar, isOnline }">
                    <div class="d-flex align-items-center">
                      <div class="position-relative mr-2">
                        <b-avatar
                          :src="getStorage(avatar)"
                          :text="getInitials(name)"
                          badge
                          :badge-variant="isOnline ? 'success' : 'secondary'"
                        />
                      </div>
                      <div>
                        <div class="font-weight-bold">
                          {{ name }}
                        </div>
                        <div class="text-muted small">
                          {{ email }}
                        </div>
                      </div>
                    </div>
                  </template>

                  <template #selected-option="{ name, avatar }">
                    <div class="d-flex align-items-center">
                      <b-avatar
                        class="mr-1"
                        :src="getStorage(avatar)"
                        :text="getInitials(name)"
                      />
                      <span>{{ name }}</span>
                    </div>
                  </template>
                </v-select>
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col md="2">
            <b-button
              class="mt-2"
              type="submit"
              variant="primary"
            >
              {{ $t('Add') }}
            </b-button>
          </b-col>
        </b-row>
      </b-overlay>
    </b-form>
  </validation-observer>
</template>

<script>
import {
  BForm,
  BFormGroup,
  BOverlay,
  BAvatar,
  BButton,
  BRow,
  BCol,
} from 'bootstrap-vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import vSelect from 'vue-select'
import projectsApi from '@/plugins/graphql/projects'
import boardsApi from '@/plugins/graphql/boards'
import { mapGetters } from 'vuex'

export default {
  name: 'AddBoardMember',
  components: {
    BRow,
    BCol,
    BForm,
    BFormGroup,
    BOverlay,
    BAvatar,
    BButton,
    ValidationObserver,
    ValidationProvider,
    vSelect,
  },
  props: {
    board: {
      type: Object,
      required: true,
    },
    members: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      selectedMembers: [],
      projectMembers: [],
      loadingUsers: false,
      isProcessing: false,
    }
  },
  computed: {
    ...mapGetters('project', ['GET_CURRENT_PROJECT']),
    availableMembers() {
      // Filter out already added board members
      const existingUuids = this.members.map(m => m.uuid)
      return this.projectMembers.filter(m => !existingUuids.includes(m.uuid))
    },
  },
  mounted() {
    this.loadProjectMembers()
  },
  methods: {
    async loadProjectMembers() {
      if (!this.board?.project?.uuid && !this.GET_CURRENT_PROJECT?.uuid) {
        return
      }

      this.loadingUsers = true
      try {
        const projectUuid = this.board?.project?.uuid || this.GET_CURRENT_PROJECT?.uuid
        const res = await projectsApi.getUsersInProject({ uuid: projectUuid, first: 100 })
        this.projectMembers = res.data.project.users.data || []
      } catch (err) {
        this.$bvToast.toast(err.message || this.$t('Failed to load project members'), {
          title: this.$t('Error'),
          variant: 'danger',
        })
      } finally {
        this.loadingUsers = false
      }
    },
    async handleAddMember(bvModalEvt) {
      if (bvModalEvt) {
        bvModalEvt.preventDefault()
      }

      const isValid = await this.$refs.form.validate()
      if (!isValid || !this.selectedMembers.length) {
        return
      }

      this.isProcessing = true

      try {
        // Add each selected member to the board
        const responses = await Promise.all(
          this.selectedMembers.map(member => boardsApi.addBoardMember({
            boardUid: this.board.uuid,
            projectUid: this.GET_CURRENT_PROJECT.uuid,
            memberUid: member.uuid,
          })),
        )

        const lastResponse = responses[responses.length - 1]
        this.showSuccess(lastResponse.data.addBoardMember.message)
        this.selectedMembers = []
        this.$refs.form.reset()

        this.$emit('updated')
        this.$bvModal.hide('add-board-member-modal')
      } catch (err) {
        this.showError(err.message || this.$t('Failed to add member(s)'))
      } finally {
        this.isProcessing = false
      }
    },
    resetForm() {
      this.selectedMembers = []
      this.$refs.form.reset()
    },
  },
}
</script>

<style scoped>
.status-badge {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid white;
}
</style>
